import { Target, TrendingUp, Users } from 'lucide-react';
import React from 'react';
import SeedIcon from '../../../assets/seed-icon.svg';

// Shades chosen to roughly match the design reference; adjust in Tailwind config if needed.
const stepStyles = [
  { bg: '#E5F6E1', text: 'You earn Seeds when others give you Twigs', textColor: 'black' },
  { bg: '#2FA94F', text: 'Invest 10-100 Seeds in any article', textColor: 'white' },
  { bg: '#0F381B', text: 'When it earns ad revenue, you get a share!', textColor: 'white' }
];

export default function Slide4() {
  return (
    <div className="animate-in slide-in-from-right duration-300">
      {/* Heading */}
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
        Invest your Seeds <img src={SeedIcon} alt="Seed" className="inline w-8 h-8 align-middle" />{' '}
        in content you believe in
      </h1>

      {/* Steps with Curly Bracket */}
      <div className="flex items-center md:justify-start mb-12 relative mt-0 md:mx-8">
        {/* Left label */}
        <div className="hidden md:flex flex-col mr-6" aria-hidden="false">
          <span className="text-lg font-semibold text-gray-900 whitespace-nowrap">
            How It Works
          </span>
        </div>
        {/* Curly bracket as a decorative symbol */}
        <div aria-hidden="true" className="hidden md:block mr-3 relative -mt-3">
          <span className="font-extralight text-[125px] leading-none text-black select-none">
            {'{'}
          </span>
        </div>
        {/* Steps */}
        <div className="flex flex-col gap-3 w-full">
          {stepStyles.map((step, i) => (
            <div key={i} className="flex items-center w-full">
              {/* Number circle outside the pill */}
              <span
                className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-extrabold text-sm shadow-sm mr-1 md:mr-3"
                style={{
                  backgroundColor: i === 0 ? '#C7E9BD' : i === 1 ? '#208A3B' : '#082411',
                  color: step.textColor
                }}
              >
                {i + 1}
              </span>
              {/* Pill container */}
              <div
                className="flex-1 rounded-full h-7 md:h-8 pl-3 pt-2 md:pt-1 shadow-sm"
                style={{ backgroundColor: step.bg }}
              >
                <p
                  className={`whitespace-nowrap text-xs md:text-base font-medium text-${step.textColor}`}
                >
                  {step.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Investment Example Card (table style inspired by Slide3) */}
      <div
        className="rounded-xl overflow-hidden shadow-sm mb-12"
        style={{
          background: 'linear-gradient(128.97deg, #F9F6FE 27.64%, #F4FDF6 99.68%)',
          border: '1px solid #DDEFD9'
        }}
      >
        <div className="bg-growth-green text-white text-sm md:text-base text-center font-semibold tracking-wide px-4 py-1">
          Investment Example
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[30%_auto_30%] divide-y sm:divide-y-0 sm:divide-x divide-[#B4B4B4] text-center py-4">
          <div className="flex flex-col justify-start md:justify-center">
            <span className="text-xs md:text-base tracking-wide mb-1 text-[#204A22] whitespace-nowrap">
              Article earns:
            </span>
            <span className="text-2xl font-heading font-bold text-[#204A22] mt-2">$1,000</span>
            <span className="text-xs md:text-base text-[#204A22] mt-1">in ad revenue</span>
          </div>
          <div className="flex flex-col justify-start md:justify-center">
            <span className="text-xs md:text-base tracking-wide mb-1 text-[#204A22] whitespace-nowrap">
              Creator shares:
            </span>
            <span className="text-2xl font-heading font-bold text-[#204A22] mt-2">20%</span>
            <span className="text-xs md:text-base text-[#204A22] mt-1">with investors ($200)</span>
          </div>
          <div className="flex flex-col justify-start md:justify-center">
            <span className="text-xs md:text-base tracking-wide mb-1 text-[#204A22] whitespace-nowrap">
              You invested:
            </span>
            <span className="text-2xl font-heading font-bold text-[#204A22] mt-2">30%</span>
            <span className="text-xs md:text-base text-[#204A22] mt-1">of all Seeds</span>
          </div>
        </div>
        <hr className="border-t border-[#B4B4B4] mx-4 md:mx-12" />
        <div className="px-4 py-2 text-center">
          <p className="text-growth-green font-semibold text-lg">
            You earn: <span className="font-bold">$60</span>
          </p>
        </div>
      </div>

      {/* Impact Section */}
      <div className="mt-4 flex flex-col items-center">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center md:text-left">
          The Impact
        </h2>
        <ul className="space-y-4 max-w-xl mx-auto md:mx-0">
          <li className="flex items-start gap-3">
            <Target className="w-5 h-5 text-growth-green mt-0.5" aria-hidden="true" />
            <span className="text-sm md:text-base text-gray-700">
              Readers scout for quality content with substance
            </span>
          </li>
          <li className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-growth-green mt-0.5" aria-hidden="true" />
            <span className="text-sm md:text-base text-gray-700">
              Good content gets boosted by investors
            </span>
          </li>
          <li className="flex items-start gap-3">
            <Users className="w-5 h-5 text-growth-green mt-0.5" aria-hidden="true" />
            <span className="text-sm md:text-base text-gray-700">
              Creators and readers win together
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
